import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Play, ClipboardList, BarChart3, Rocket,
  MessageCircle, Dumbbell, Watch, FlaskConical, Trophy,
  Star, TrendingUp, ChevronDown, ChevronUp,
  Compass, BarChart2, Diamond, CheckCircle2, Leaf, Activity
} from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import ThreeBackground from '../components/ThreeBackground';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';
import { COLORS, STEPS, FEATURES, TESTIMONIALS, TIERS, FAQS, STATS } from '../data/mockData';
import { isPrelaunch, LAUNCH_DATE_DISPLAY } from '../data/launch';

const ICON_MAP = {
  ClipboardList, BarChart3, Rocket, MessageCircle, Dumbbell,
  Watch, FlaskConical, Trophy, Compass, BarChart2, TrendingUp, Diamond, Activity,
};

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      style={{
        width: '100%', background: COLORS.surface, borderRadius: 16, padding: 20, marginBottom: 12,
        border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.2s',
        boxShadow: open ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary, flex: 1, paddingRight: 12, fontFamily: 'Outfit, sans-serif' }}>{question}</span>
        {open ? <ChevronUp size={20} color={COLORS.primary} /> : <ChevronDown size={20} color={COLORS.primary} />}
      </div>
      {open && (
        <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.6, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}40` }}>
          {answer}
        </p>
      )}
    </button>
  );
}

export default function LandingPage() {
  const sectionsRef = useRef({});

  const scrollToSection = useCallback((name) => {
    const el = sectionsRef.current[name];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const markRef = (name) => (el) => { if (el) sectionsRef.current[name] = el; };

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader onScrollTo={scrollToSection} />

      {/* PRE-LAUNCH NOTICE */}
      {isPrelaunch() && (
        <section style={{ padding: '20px 20px 0' }}>
          <div data-testid="prelaunch-banner" style={{
            maxWidth: 860, margin: '0 auto', textAlign: 'center',
            backgroundColor: '#FFEBEE', border: '3px solid #C62828', borderRadius: 16,
            padding: '22px 26px', boxShadow: '0 4px 16px rgba(198,40,40,0.12)',
          }}>
            <p style={{
              color: '#C62828', fontWeight: 900, letterSpacing: 0.5,
              fontSize: 'clamp(17px, 2.6vw, 24px)', lineHeight: 1.35, margin: 0,
              fontFamily: 'Outfit, sans-serif', textTransform: 'uppercase',
            }}>
              The site will become fully accessible in the {LAUNCH_DATE_DISPLAY}.
            </p>
            <p style={{ color: '#C62828', fontWeight: 700, fontSize: 14, margin: '10px 0 0' }}>
              New member signups and purchases are temporarily unavailable until then.
            </p>
          </div>
        </section>
      )}

      {/* INTRO VIDEO (under logo) */}
      <section
        data-testid="landing-intro-video-section"
        style={{
          background: '#F9F6F0',
          padding: '20px 20px 0',
        }}
      >
        {/* Avatar_RCohen intro — autoplays once (muted per browser policy),
            20% larger than the original 252px presentation. The clip is
            portrait (9:16), so the slot matches its native aspect. */}
        <VideoWithSoundToggle
          src="/media/hero-intro.mp4"
          testId="landing-intro-video"
          aspectRatio="9 / 16"
          maxWidth={302}
          objectFit="cover"
        />
      </section>

      {/* HERO */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 40%, #DDE4C7 70%, #F9F6F0 100%)', padding: '48px 20px 56px', position: 'relative', overflow: 'hidden', minHeight: 520 }}>
        {/* Three.js Animated Background */}
        <ThreeBackground />
        <div style={{ maxWidth: 1140, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 48, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <div style={{ flex: '1 1 480px', minWidth: 280 }}>
            <div style={{ display: 'inline-block', backgroundColor: '#E8F5E9', padding: '6px 14px', borderRadius: 9999, marginBottom: 20 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.primary }}>7-Day Free Trial — No Credit Card</span>
            </div>
            <h1 style={{ fontSize: 'clamp(38px, 5vw, 52px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.12, letterSpacing: -1.5, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>
              Live Longer.<br />Live Better.
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 19px)', color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 28, maxWidth: 520 }}>
              Evidence-based health optimization powered by AI. Track 100+ longevity factors, get personalized coaching, and predict your healthspan.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/register" className="hero-primary-btn" style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px 28px', display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: '#FFF' }}>Get Started Free</span>
                <ArrowRight size={20} color="#FFF" />
              </Link>
              <button onClick={() => scrollToSection('howItWorks')} style={{ borderRadius: 9999, padding: '14px 24px', display: 'inline-flex', alignItems: 'center', gap: 8, border: `1.5px solid ${COLORS.primary}`, backgroundColor: 'transparent', cursor: 'pointer', transition: 'background-color 0.2s' }}>
                <Play size={20} color={COLORS.primary} />
                <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.primary }}>How It Works</span>
              </button>
            </div>
          </div>

          {/* Hero Visual Card */}
          <div className="hero-visual" style={{ flex: '0 1 300px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 260, borderRadius: 20, padding: 28, background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 12px 40px rgba(107,142,35,0.3)' }}>
              <Activity size={36} color="#FFF" />
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', marginTop: 12 }}>Health Score</span>
              <span style={{ fontSize: 56, fontWeight: 900, color: '#FFF', marginTop: 4, fontFamily: 'Outfit, sans-serif' }}>87</span>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>+12 pts this month</span>
              <div style={{ width: '100%', height: 6, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 3, marginTop: 16 }}>
                <div style={{ height: 6, width: '87%', backgroundColor: '#FFF', borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 16, textAlign: 'center', width: 124, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <TrendingUp size={20} color={COLORS.success} />
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 6 }}>Longevity</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, marginTop: 2, fontFamily: 'Outfit, sans-serif' }}>91 yrs</div>
              </div>
              <div style={{ backgroundColor: COLORS.surface, borderRadius: 14, padding: 16, textAlign: 'center', width: 124, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Dumbbell size={20} color={COLORS.secondary} />
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 6 }}>Exercise</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, marginTop: 2, fontFamily: 'Outfit, sans-serif' }}>85/100</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section style={{ backgroundColor: COLORS.textPrimary, padding: '28px 20px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          {STATS.map((st, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>{st.num}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>{st.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section ref={markRef('howItWorks')} style={{ padding: '56px 20px', scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2, textTransform: 'uppercase' }}>HOW IT WORKS</span>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, marginBottom: 10, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Three Steps to a Longer Life</h2>
          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
            {STEPS.map((step, i) => {
              const IconComp = ICON_MAP[step.icon];
              return (
                <div key={i} style={{ flex: '1 1 280px', maxWidth: 380, backgroundColor: COLORS.surface, borderRadius: 24, padding: 28, textAlign: 'center', position: 'relative', transition: 'transform 0.3s, box-shadow 0.3s' }}
                  className="hover-lift"
                >
                  <span style={{ position: 'absolute', top: 12, left: 16, fontSize: 40, fontWeight: 900, color: `${COLORS.primary}15`, fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                  <div style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    {IconComp && <IconComp size={28} color={COLORS.primary} />}
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8, fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                  <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section ref={markRef('features')} style={{ padding: '56px 20px', backgroundColor: COLORS.surfaceSecondary, scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>FEATURES</span>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, marginBottom: 10, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Everything You Need to Optimize Your Health</h2>
          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
            {FEATURES.map((f, i) => {
              const IconComp = ICON_MAP[f.icon];
              return (
                <div key={i} style={{ flex: '1 1 280px', maxWidth: 360, backgroundColor: COLORS.surface, borderRadius: 16, padding: 24, textAlign: 'left', transition: 'transform 0.3s, box-shadow 0.3s' }} className="hover-lift">
                  <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    {IconComp && <IconComp size={26} color={COLORS.primary} />}
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 6, fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.5, margin: 0 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: '56px 20px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>TESTIMONIALS</span>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, marginBottom: 10, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Loved by Health Optimizers</h2>
          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ flex: '1 1 300px', maxWidth: 400, backgroundColor: COLORS.surface, borderRadius: 24, padding: 24, textAlign: 'left', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'transform 0.3s, box-shadow 0.3s' }} className="hover-lift">
                <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} size={16} color="#F5A623" fill="#F5A623" />
                  ))}
                </div>
                <p style={{ fontSize: 15, color: COLORS.textPrimary, lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>"{t.quote}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${COLORS.border}40` }}>
                  <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#FFF' }}>{t.init}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{t.age} · {t.tier} Member</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#E8F5E9', padding: '5px 10px', borderRadius: 9999 }}>
                    <TrendingUp size={14} color={COLORS.success} />
                    <span style={{ fontSize: 14, fontWeight: 800, color: COLORS.success }}>{t.score}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section ref={markRef('pricing')} style={{ padding: '56px 20px', backgroundColor: COLORS.surfaceSecondary, scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>PRICING</span>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, marginBottom: 10, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Choose Your Plan</h2>
          <p style={{ fontSize: 16, color: COLORS.textSecondary, marginBottom: 32 }}>Start with a 7-day free Analyst trial. Upgrade anytime.</p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch' }}>
            {TIERS.map((t, i) => {
              const IconComp = ICON_MAP[t.icon];
              return (
                <div key={i} style={{
                  flex: '1 1 240px', maxWidth: 280, backgroundColor: COLORS.surface, borderRadius: 24, padding: 24,
                  border: t.popular ? `2px solid ${COLORS.tierOptimizer}` : `1px solid ${COLORS.border}`,
                  position: 'relative', textAlign: 'left', transition: 'transform 0.3s, box-shadow 0.3s',
                }} className="hover-lift">
                  {t.popular && (
                    <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', backgroundColor: COLORS.tierOptimizer, padding: '4px 14px', borderRadius: 9999 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#FFF' }}>Most Popular</span>
                    </div>
                  )}
                  <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: `${t.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    {IconComp && <IconComp size={28} color={t.color} />}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{t.name}</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: COLORS.textPrimary, marginTop: 4, fontFamily: 'Outfit, sans-serif' }}>
                    {t.price}{t.sub && <span style={{ fontSize: 14, fontWeight: 400, color: COLORS.textSecondary }}>{t.sub}</span>}
                  </div>
                  <div style={{ marginTop: 20, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {t.features.map((f, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle2 size={16} color={t.color} />
                        <span style={{ fontSize: 14, color: COLORS.textPrimary }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/register" style={{
                    display: 'block', textAlign: 'center', backgroundColor: t.color, borderRadius: 9999, padding: '14px',
                    fontSize: 15, fontWeight: 700, color: '#FFF', textDecoration: 'none', transition: 'opacity 0.2s',
                  }}>
                    {t.price === 'Free' ? 'Start Free' : 'Start Trial'}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '56px 20px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, letterSpacing: 2 }}>FAQ</span>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, color: COLORS.textPrimary, marginTop: 12, marginBottom: 32, letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Frequently Asked Questions</h2>
          {FAQS.map((faq, i) => (
            <FAQItem key={i} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </section>

      {/* CTA BOTTOM */}
      <section style={{ background: 'linear-gradient(135deg, #6B8E23, #4a6e18)', padding: '56px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: '#FFF', letterSpacing: -0.5, fontFamily: 'Outfit, sans-serif' }}>Ready to Start Your Longevity Journey?</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', marginTop: 10, marginBottom: 28 }}>Join thousands optimizing their healthspan with ThriveLonger.ai</p>
          <Link to="/register" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, backgroundColor: '#FFF', borderRadius: 9999,
            padding: '16px 32px', textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s',
          }} className="hero-primary-btn">
            <span style={{ fontSize: 17, fontWeight: 700, color: COLORS.primary }}>Get Started — It's Free</span>
            <ArrowRight size={20} color={COLORS.primary} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
